/**
 * <BuyerBuyBoxesPanel buyerId={id} buyerName={name} />
 *
 * Buy-box CRUD for a single Cash Buyer. Lives on the BuyerDetail page.
 *
 * Endpoints:
 *   GET    /api/team/{member_id}/buy-boxes
 *   POST   /api/team/{member_id}/buy-boxes
 *   PATCH  /api/buy-boxes/{box_id}
 *   DELETE /api/buy-boxes/{box_id}
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, X, Target, ToggleLeft, ToggleRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney } from "@/lib/api";

const EMPTY_BOX = {
  name: "",
  active: true,
  states: [],
  cities: [],
  zips: [],
  min_beds: "",
  max_beds: "",
  min_baths: "",
  max_baths: "",
  min_sqft: "",
  max_sqft: "",
  min_price: "",
  max_price: "",
  min_arv: "",
  max_arv: "",
  property_types: [],
  notes: "",
};

export default function BuyerBuyBoxesPanel({ buyerId, buyerName }) {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // {mode: "create"|"edit", initial: box}

  const load = useCallback(async () => {
    if (!buyerId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/team/${buyerId}/buy-boxes`);
      setBoxes(Array.isArray(data) ? data : []);
    } catch {
      setBoxes([]);
    }
    setLoading(false);
  }, [buyerId]);

  useEffect(() => { load(); }, [load]);

  const onCreate = () => setEditor({ mode: "create", initial: EMPTY_BOX });
  const onEdit = (box) => setEditor({ mode: "edit", initial: box });

  const onToggleActive = async (box) => {
    try {
      await api.patch(`/buy-boxes/${box.id}`, { active: !box.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const onDelete = async (box) => {
    if (!window.confirm(`Delete buy-box "${box.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/buy-boxes/${box.id}`);
      toast.success("Buy-box deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="card mb-5" data-testid="buyer-buy-boxes-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Target size={11} className="text-indigo-600" /> Community · Layer 3
          </div>
          <div className="font-display font-bold text-lg mt-0.5">Buy-boxes</div>
          <div className="text-xs text-[var(--ink-3)]">
            Standing intent — every new lead is auto-matched against these criteria.
          </div>
        </div>
        <button
          type="button"
          className="btn-money"
          onClick={onCreate}
          data-testid="buy-box-create-btn"
        >
          <Plus size={14} /> Add buy-box
        </button>
      </div>

      {loading ? (
        <div className="text-center py-6 text-sm text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin inline mr-2" /> Loading buy-boxes…
        </div>
      ) : boxes.length === 0 ? (
        <div className="text-center py-8 text-sm text-[var(--ink-3)] border-2 border-dashed border-slate-200 rounded-lg" data-testid="buy-box-empty">
          <Target size={26} className="mx-auto opacity-40 mb-2" />
          <p>No buy-boxes yet for <strong>{buyerName || "this buyer"}</strong>.</p>
          <p className="text-[11px] mt-1">Add one to start auto-matching deals from your pipeline.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="buy-box-list">
          {boxes.map((b) => (
            <div
              key={b.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${b.active ? "border-indigo-200 bg-indigo-50/40" : "border-[var(--line)] bg-slate-50 opacity-70"}`}
              data-testid={`buy-box-row-${b.id}`}
            >
              <button
                type="button"
                onClick={() => onToggleActive(b)}
                className="shrink-0 mt-0.5 text-indigo-600 hover:text-indigo-800"
                title={b.active ? "Disable" : "Enable"}
                data-testid={`buy-box-toggle-${b.id}`}
              >
                {b.active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm flex items-center gap-2">
                  {b.name}
                  {!b.active && <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">paused</span>}
                </div>
                <div className="text-[11px] text-[var(--ink-3)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {b.states?.length > 0 && <span>📍 {b.states.join(", ")}</span>}
                  {b.cities?.length > 0 && <span>🏙️ {b.cities.join(", ")}</span>}
                  {(b.min_price != null || b.max_price != null) && (
                    <span>💰 {b.min_price != null ? fmtMoney(b.min_price) : "$0"} – {b.max_price != null ? fmtMoney(b.max_price) : "any"}</span>
                  )}
                  {(b.min_beds != null || b.max_beds != null) && (
                    <span>🛏️ {b.min_beds ?? "—"}–{b.max_beds ?? "any"} bd</span>
                  )}
                  {b.property_types?.length > 0 && <span>🏠 {b.property_types.join(", ")}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => onEdit(b)}
                  className="btn-ghost p-1.5"
                  title="Edit"
                  data-testid={`buy-box-edit-${b.id}`}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(b)}
                  className="btn-ghost p-1.5 text-rose-600"
                  title="Delete"
                  data-testid={`buy-box-delete-${b.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editor && (
        <BuyBoxEditor
          buyerId={buyerId}
          mode={editor.mode}
          initial={editor.initial}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Editor modal — same shape for create + edit; create POSTs to
// /team/{member_id}/buy-boxes, edit PATCHes /buy-boxes/{box_id}.
// ─────────────────────────────────────────────────────────────────────────
function BuyBoxEditor({ buyerId, mode, initial, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    ...EMPTY_BOX,
    ...initial,
    // arrays back from server arrive as arrays; the editor renders them
    // as comma-separated strings for ergonomics, then re-splits on save.
    states_str: (initial.states || []).join(", "),
    cities_str: (initial.cities || []).join(", "),
    zips_str: (initial.zips || []).join(", "),
    property_types_str: (initial.property_types || []).join(", "),
    min_beds: initial.min_beds ?? "",
    max_beds: initial.max_beds ?? "",
    min_baths: initial.min_baths ?? "",
    max_baths: initial.max_baths ?? "",
    min_sqft: initial.min_sqft ?? "",
    max_sqft: initial.max_sqft ?? "",
    min_price: initial.min_price ?? "",
    max_price: initial.max_price ?? "",
    min_arv: initial.min_arv ?? "",
    max_arv: initial.max_arv ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError("");
    const toCsvList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
    const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
    const body = {
      name: form.name.trim(),
      active: !!form.active,
      states: toCsvList(form.states_str).map((s) => s.toUpperCase()),
      cities: toCsvList(form.cities_str),
      zips: toCsvList(form.zips_str),
      property_types: toCsvList(form.property_types_str),
      min_beds: numOrNull(form.min_beds),
      max_beds: numOrNull(form.max_beds),
      min_baths: numOrNull(form.min_baths),
      max_baths: numOrNull(form.max_baths),
      min_sqft: numOrNull(form.min_sqft),
      max_sqft: numOrNull(form.max_sqft),
      min_price: numOrNull(form.min_price),
      max_price: numOrNull(form.max_price),
      min_arv: numOrNull(form.min_arv),
      max_arv: numOrNull(form.max_arv),
      notes: form.notes || "",
    };
    try {
      if (isEdit) await api.patch(`/buy-boxes/${initial.id}`, body);
      else await api.post(`/team/${buyerId}/buy-boxes`, body);
      toast.success(isEdit ? "Buy-box updated" : "Buy-box created");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="buy-box-editor">
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow">{isEdit ? "Edit buy-box" : "New buy-box"}</div>
            <h2 className="font-display font-bold text-xl mt-1">{isEdit ? initial.name : "Define what this buyer wants"}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="buy-box-editor-close"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-4" data-testid="buy-box-form">
          <div className="md:col-span-2">
            <label className="field-label">Buy-box name *</label>
            <input className="input" value={form.name} onChange={set("name")} placeholder="DFW SFH < $200k" data-testid="buy-box-name" />
          </div>

          <div className="md:col-span-2">
            <label className="field-label">States (comma-separated, e.g. TX, OK)</label>
            <input className="input" value={form.states_str} onChange={set("states_str")} placeholder="TX, OK" data-testid="buy-box-states" />
          </div>
          <div><label className="field-label">Cities</label><input className="input" value={form.cities_str} onChange={set("cities_str")} placeholder="Dallas, Fort Worth" data-testid="buy-box-cities" /></div>
          <div><label className="field-label">ZIPs</label><input className="input" value={form.zips_str} onChange={set("zips_str")} placeholder="75204, 75217" data-testid="buy-box-zips" /></div>

          <div className="md:col-span-2 divider my-1" />

          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">Min price</label><input className="input" type="number" min="0" value={form.min_price} onChange={set("min_price")} data-testid="buy-box-min-price" /></div>
            <div><label className="field-label">Max price</label><input className="input" type="number" min="0" value={form.max_price} onChange={set("max_price")} data-testid="buy-box-max-price" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">Min ARV</label><input className="input" type="number" min="0" value={form.min_arv} onChange={set("min_arv")} data-testid="buy-box-min-arv" /></div>
            <div><label className="field-label">Max ARV</label><input className="input" type="number" min="0" value={form.max_arv} onChange={set("max_arv")} data-testid="buy-box-max-arv" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">Min beds</label><input className="input" type="number" min="0" value={form.min_beds} onChange={set("min_beds")} data-testid="buy-box-min-beds" /></div>
            <div><label className="field-label">Max beds</label><input className="input" type="number" min="0" value={form.max_beds} onChange={set("max_beds")} data-testid="buy-box-max-beds" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">Min baths</label><input className="input" type="number" min="0" step="0.5" value={form.min_baths} onChange={set("min_baths")} data-testid="buy-box-min-baths" /></div>
            <div><label className="field-label">Max baths</label><input className="input" type="number" min="0" step="0.5" value={form.max_baths} onChange={set("max_baths")} data-testid="buy-box-max-baths" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">Min sqft</label><input className="input" type="number" min="0" value={form.min_sqft} onChange={set("min_sqft")} data-testid="buy-box-min-sqft" /></div>
            <div><label className="field-label">Max sqft</label><input className="input" type="number" min="0" value={form.max_sqft} onChange={set("max_sqft")} data-testid="buy-box-max-sqft" /></div>
          </div>

          <div className="md:col-span-2">
            <label className="field-label">Property types (comma-separated)</label>
            <input className="input" value={form.property_types_str} onChange={set("property_types_str")} placeholder="SFR, Multi, Townhouse" data-testid="buy-box-types" />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Notes (private)</label>
            <textarea className="textarea" rows={2} value={form.notes} onChange={set("notes")} data-testid="buy-box-notes" />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input
              id="buy-box-active"
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              data-testid="buy-box-active"
            />
            <label htmlFor="buy-box-active" className="text-sm">Active (matches will fire)</label>
          </div>

          {error && <div className="md:col-span-2 banner-err" data-testid="buy-box-error">{String(error)}</div>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-[var(--line)]">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-money" disabled={saving} data-testid="buy-box-save">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create buy-box"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
